import { cn } from "@-/com.npmjs.cn";
import type { ReactNode } from "react";
import { WebsiteBrand } from "#website-brand";
import { WebsiteButtonLink } from "#website-button";
import { WebsiteContainer } from "#website-container";
import { websiteContainerVariants } from "#website-container-variants";
import { WebsiteGlow } from "#website-glow";
import { LOG_IN_PATH } from "#website-store-links";
import { WebsiteEyebrow, WebsiteHeading, WebsiteText } from "#website-typography";
import { useWebsiteGetAppHref } from "#use-website-get-app-href";

type WebsiteNavProps = {
  /** Where "Get the app" goes on a desktop; phones always go to their store. */
  downloadHref?: string;
  homeHref?: string;
};

/**
 * The only route into the web app is a quiet "Log in" link for people who already have an account;
 * every promoted action downloads the native app. There is deliberately no sign-up link.
 */
export function WebsiteNav({ downloadHref, homeHref = "/" }: WebsiteNavProps) {
  const getAppHref = useWebsiteGetAppHref(downloadHref);

  return (
    <nav
      aria-label="Main navigation"
      className={cn(
        websiteContainerVariants(),
        "relative z-20 flex items-center justify-between py-4 sm:py-5",
      )}
    >
      <a aria-label="Haniverse home" className="text-inherit no-underline" href={homeHref}>
        <WebsiteBrand />
      </a>
      <div className="flex items-center gap-2 sm:gap-3">
        <WebsiteButtonLink href={LOG_IN_PATH} rel="nofollow" variant="ghost">
          Log in
        </WebsiteButtonLink>
        {/* Phones get a download bar or the page's own store badges instead. */}
        <WebsiteButtonLink
          className="hidden hover:translate-y-0 hover:bg-website-accent hover:text-black sm:inline-flex"
          href={getAppHref}
        >
          Get the app
        </WebsiteButtonLink>
      </div>
    </nav>
  );
}

/**
 * The top of every page: the warm canvas, its glows and the main navigation, followed by the
 * page's own opening content.
 */
export function WebsiteHeader({
  children,
  className,
  ...navProps
}: WebsiteNavProps & { children: ReactNode; className?: string }) {
  return (
    <header
      className={cn("relative isolate overflow-hidden bg-website-canvas text-black", className)}
      id="top"
    >
      <WebsiteGlow className="-top-48 -right-40 size-[44rem]" tone="faint" />
      <WebsiteGlow className="top-1/2 -left-64 size-[36rem]" tone="warm" />
      <WebsiteNav {...navProps} />
      {children}
    </header>
  );
}

/** The header of every page other than the landing: an eyebrow, the page title and a lead. */
export function WebsitePageHeader({
  children,
  description,
  eyebrow,
  title,
}: {
  children?: ReactNode;
  description?: ReactNode;
  eyebrow: string;
  title: string;
}) {
  return (
    <WebsiteHeader>
      <WebsiteContainer className="pt-10 pb-16 sm:pt-16 sm:pb-24">
        <WebsiteEyebrow>{eyebrow}</WebsiteEyebrow>
        <WebsiteHeading className="mt-4 max-w-[18ch]" level={1} size="section">
          {title}
        </WebsiteHeading>
        {description === undefined ? null : (
          <WebsiteText className="mt-5 max-w-[36rem]" size="lead">
            {description}
          </WebsiteText>
        )}
        {children}
      </WebsiteContainer>
    </WebsiteHeader>
  );
}
